import { useEffect, useState } from "react";
import { useTenant } from "@/lib/tenant";
import { toast } from "sonner";
import { Plus, Building2, Trash2, CheckCircle2, AlertCircle, Save, BookOpen, ExternalLink, ChevronDown, ChevronRight, Copy, Download, Upload, Archive } from "lucide-react";

const blank = () => ({ name: "", tenant_id: "", client_id: "", client_secret: "", domain: "" });

const PERMISSIONS = [
  "User.Read.All",
  "Organization.Read.All",
  "Mail.Send",
  "Exchange.ManageAsApp",
];

function Step({ n, title, children, last }) {
  return (
    <div className="flex gap-3">
      <div className="shrink-0 w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 flex items-center justify-center text-xs font-bold">{n}</div>
      <div className={`flex-1 ${last ? "" : "pb-4 border-b border-white/5"}`}>
        <div className="text-sm font-semibold text-slate-100">{title}</div>
        <div className="text-[13px] text-slate-300 mt-1.5 leading-relaxed space-y-1.5">{children}</div>
      </div>
    </div>
  );
}

function CopyBox({ text, testId }) {
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied"); }}
      data-testid={testId}
      className="font-mono text-[11px] bg-white/[0.04] border border-white/10 px-2 py-1 rounded inline-flex items-center gap-1.5 hover:bg-white/[0.08]">
      {text} <Copy size={10} className="text-slate-400" />
    </button>
  );
}

function SetupGuide({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="glass p-6 mb-4 border border-cyan-500/20 bg-cyan-500/[0.03]">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 text-cyan-300 text-xs uppercase tracking-widest mb-1"><BookOpen size={13} /> Setup Guide</div>
          <h2 className="text-lg font-bold">Adding a new tenant — step by step</h2>
          <p className="text-slate-400 text-xs mt-1">Each new tenant needs its own Azure AD App Registration. Allow ~10 min per tenant the first time, ~3 min once you're familiar.</p>
        </div>
        <button onClick={onClose} className="btn text-xs" data-testid="close-guide">Close</button>
      </div>

      <div className="space-y-4">
        <Step n={1} title="Sign in to Azure Portal as a Global Admin of the new tenant">
          <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="btn !py-1 !px-2 text-[11px] inline-flex">
            <ExternalLink size={11} /> Open portal.azure.com
          </a>
          <div>Sign in with a <strong>Global Admin</strong> account belonging to the tenant you want to add (not the one you're already managing).</div>
        </Step>

        <Step n={2} title='Go to App registrations → "+ New registration"'>
          <div>In the top search bar, type <CopyBox text="App registrations" testId="copy-search-app-reg" /> and select it.</div>
          <div>Click <strong>+ New registration</strong> at the top left.</div>
        </Step>

        <Step n={3} title="Register the app">
          <div>Fill in:</div>
          <ul className="ml-5 list-disc text-[12.5px] space-y-0.5">
            <li><strong>Name</strong>: <CopyBox text="Wilson Contractors · M365" testId="copy-app-name" /></li>
            <li><strong>Supported account types</strong>: select <em>"Accounts in this organizational directory only (Single tenant)"</em></li>
            <li><strong>Redirect URI</strong>: leave blank</li>
          </ul>
          <div>Click <strong>Register</strong>.</div>
        </Step>

        <Step n={4} title="Copy 3 values from the Overview page">
          <div>On the app's Overview screen, copy these (you'll paste them into the form below):</div>
          <ul className="ml-5 list-disc text-[12.5px] space-y-0.5">
            <li><strong>Application (client) ID</strong></li>
            <li><strong>Directory (tenant) ID</strong></li>
            <li>Your tenant's primary domain (e.g. <CopyBox text="yourcompany.com" testId="copy-domain-example" />)</li>
          </ul>
        </Step>

        <Step n={5} title="Create a Client Secret">
          <div>Left menu → <strong>Certificates &amp; secrets</strong> → <strong>+ New client secret</strong></div>
          <ul className="ml-5 list-disc text-[12.5px] space-y-0.5">
            <li><strong>Description</strong>: <CopyBox text="signature-manager-secret" testId="copy-secret-name" /></li>
            <li><strong>Expires</strong>: 24 months</li>
          </ul>
          <div className="text-amber-300/90 text-[12px] bg-amber-500/[0.06] border border-amber-500/20 rounded px-2 py-1.5 mt-1">
            ⚠️ <strong>IMMEDIATELY copy the "Value"</strong> (not the Secret ID). It's only shown once. Paste it into the form below within 5 minutes.
          </div>
        </Step>

        <Step n={6} title="Add Microsoft Graph API permissions">
          <div>Left menu → <strong>API permissions</strong> → <strong>+ Add a permission</strong> → <strong>Microsoft Graph</strong> → <strong>Application permissions</strong></div>
          <div>Search for and check each of these:</div>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {PERMISSIONS.slice(0, 3).map(p => <CopyBox key={p} text={p} testId={`copy-perm-${p}`} />)}
          </div>
          <div>Click <strong>Add permissions</strong>.</div>
        </Step>

        <Step n={7} title="Add Exchange Online permission">
          <div>Click <strong>+ Add a permission</strong> again → <strong>APIs my organization uses</strong> → search <CopyBox text="Office 365 Exchange Online" testId="copy-exo" /> → click it.</div>
          <div>Click <strong>Application permissions</strong> and check:</div>
          <div className="mt-1"><CopyBox text="Exchange.ManageAsApp" testId="copy-perm-exo" /></div>
          <div>Click <strong>Add permissions</strong>.</div>
        </Step>

        <Step n={8} title="⚠️ Grant Admin Consent">
          <div>At the top of the API permissions list, click <strong>"Grant admin consent for [tenant name]"</strong> → confirm.</div>
          <div>All 4 permissions should turn <span className="text-emerald-400">✓ Granted</span>.</div>
        </Step>

        <Step n={9} title="Assign Exchange Administrator role to the app">
          <div>Open <a href="https://entra.microsoft.com" target="_blank" rel="noopener noreferrer" className="text-emerald-300 underline">entra.microsoft.com</a> → <strong>Roles &amp; admins</strong> → <strong>Roles &amp; administrators</strong>.</div>
          <div>Search for <CopyBox text="Exchange Administrator" testId="copy-role-name" /> → click it.</div>
          <div>Click <strong>+ Add assignments</strong> → click <em>"No member selected"</em> → search for the Wilson Contractors app you just registered → select it → <strong>Select</strong> → <strong>Next</strong> → <strong>Assign</strong>.</div>
          <div className="text-[11px] text-slate-500">This is what allows the app to create transport rules and manage OWA signatures.</div>
        </Step>

        <Step n={10} title="Paste credentials below and click Add Tenant" last>
          <div>Back here in the app, click <strong>+ Add Tenant</strong> and paste:</div>
          <ul className="ml-5 list-disc text-[12.5px] space-y-0.5">
            <li><strong>Azure Tenant ID</strong> — from step 4</li>
            <li><strong>Application (Client) ID</strong> — from step 4</li>
            <li><strong>Client Secret</strong> — the "Value" you copied in step 5</li>
            <li><strong>Domain</strong> — e.g. <em>yourcompany.com</em></li>
            <li>Name auto-fills from M365 if left blank</li>
          </ul>
          <div className="text-emerald-300/90 text-[12px] bg-emerald-500/[0.06] border border-emerald-500/20 rounded px-2 py-1.5 mt-1">
            ✅ When you click <strong>Add &amp; Verify</strong>, the app immediately tests the credentials by querying Microsoft Graph. If anything is wrong (typo, missing consent, missing role), you'll get a clear error — no waiting around.
          </div>
        </Step>
      </div>

      <div className="mt-5 pt-4 border-t border-white/5 text-xs text-slate-400">
        <div className="font-semibold text-slate-300 mb-1.5">Common gotchas</div>
        <ul className="ml-4 list-disc space-y-1">
          <li><strong>"Tenant not found"</strong> — you swapped Tenant ID and Client ID (both look like GUIDs). Re-check on the Overview page.</li>
          <li><strong>"Forbidden / Insufficient privileges"</strong> — you forgot to click "Grant admin consent" in step 8.</li>
          <li><strong>"Exchange RBAC denied"</strong> — Exchange Administrator role not assigned (step 9). Allow ~10 min for the role to propagate before retrying.</li>
          <li><strong>Secret pasted with leading/trailing space</strong> — copy carefully; even a trailing newline breaks auth.</li>
        </ul>
      </div>
    </div>
  );
}

function BackupRestore() {
  const { api, refreshTenants } = useTenant();
  const [summary, setSummary] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [mode, setMode] = useState("merge");

  const loadSummary = async () => {
    try {
      const r = await api.get("/admin/export/summary");
      setSummary(r.data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadSummary(); }, []); // eslint-disable-line

  const fmtBytes = (n) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      // Use plain fetch so we can stream the (potentially large) JSON as a blob
      const url = `${api.defaults.baseURL}/admin/export`;
      const headers = { ...api.defaults.headers.common };
      const r = await fetch(url, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      const dl = URL.createObjectURL(blob);
      a.href = dl;
      a.download = `signature-manager-backup-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(dl);
      toast.success("Backup downloaded — keep this file safe");
    } catch (e) {
      toast.error("Backup failed: " + e.message);
    } finally { setDownloading(false); }
  };

  const restoreBackup = async () => {
    if (!restoreFile) { toast.error("Choose a backup file first"); return; }
    const confirmMsg = mode === "replace"
      ? "REPLACE mode will WIPE all existing tenants/templates/banners/settings first. Continue?"
      : "MERGE mode will upsert all data from the backup. Existing records with matching IDs will be overwritten. Continue?";
    if (!window.confirm(confirmMsg)) return;
    setRestoring(true);
    try {
      const data = new FormData();
      data.append("payload", restoreFile);
      data.append("mode", mode);
      const r = await api.post("/admin/import", data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Restored ${r.data.files} files + ${Object.keys(r.data.collections).length} collections`);
      setRestoreFile(null);
      await refreshTenants();
      await loadSummary();
    } catch (e) {
      toast.error("Restore failed: " + (e.response?.data?.detail || e.message));
    } finally { setRestoring(false); }
  };

  return (
    <div className="glass p-5 mt-6">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-widest mb-1">
            <Archive size={13} /> Backup &amp; Restore
          </div>
          <h2 className="text-base font-semibold">Migrate to production / disaster recovery</h2>
          <p className="text-slate-400 text-xs mt-1 max-w-2xl">
            Download a complete snapshot (all tenants, templates, banners, brand settings and uploaded files). Use this to migrate from preview to production, or as a routine backup.
          </p>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 text-xs">
          <div className="bg-white/[0.03] border border-white/5 rounded px-3 py-2">
            <div className="text-slate-500">Tenants</div>
            <div className="text-lg font-bold text-slate-100">{summary.collections.tenants}</div>
          </div>
          <div className="bg-white/[0.03] border border-white/5 rounded px-3 py-2">
            <div className="text-slate-500">Templates</div>
            <div className="text-lg font-bold text-slate-100">{summary.collections.templates}</div>
          </div>
          <div className="bg-white/[0.03] border border-white/5 rounded px-3 py-2">
            <div className="text-slate-500">Banners</div>
            <div className="text-lg font-bold text-slate-100">{summary.collections.banners}</div>
          </div>
          <div className="bg-white/[0.03] border border-white/5 rounded px-3 py-2">
            <div className="text-slate-500">Uploaded files</div>
            <div className="text-lg font-bold text-slate-100">{summary.totals.files} <span className="text-[10px] text-slate-500">({fmtBytes(summary.totals.bytes)})</span></div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-white/5 rounded-lg p-4 bg-white/[0.02]">
          <div className="flex items-center gap-2 mb-2"><Download size={14} className="text-emerald-400" /><span className="text-sm font-semibold">Download backup</span></div>
          <p className="text-xs text-slate-400 mb-3">Bundles tenants, templates, banners, settings and all uploaded logos/images into a single JSON file.</p>
          <button onClick={downloadBackup} disabled={downloading} className="btn btn-primary w-full justify-center" data-testid="download-backup">
            <Download size={14} /> {downloading ? "Bundling…" : "Download backup"}
          </button>
        </div>

        <div className="border border-white/5 rounded-lg p-4 bg-white/[0.02]">
          <div className="flex items-center gap-2 mb-2"><Upload size={14} className="text-cyan-400" /><span className="text-sm font-semibold">Restore from backup</span></div>
          <p className="text-xs text-slate-400 mb-3">Upload a previously downloaded backup file to restore everything.</p>
          <input type="file" accept=".json,application/json" onChange={e => setRestoreFile(e.target.files?.[0] || null)}
            className="block w-full text-xs text-slate-300 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-white/10 file:text-slate-200 file:cursor-pointer mb-2"
            data-testid="restore-file" />
          <div className="flex items-center gap-3 text-xs mb-2">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={mode === "merge"} onChange={() => setMode("merge")} data-testid="mode-merge" />
              <span>Merge (safe)</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} data-testid="mode-replace" />
              <span className="text-red-300">Replace (wipe first)</span>
            </label>
          </div>
          <button onClick={restoreBackup} disabled={restoring || !restoreFile} className="btn w-full justify-center" data-testid="restore-backup">
            <Upload size={14} /> {restoring ? "Restoring…" : "Restore"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Tenants() {
  const { tenants, refreshTenants, api, tenantId, setTenantId } = useTenant();
  const [form, setForm] = useState(blank());
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [showGuide, setShowGuide] = useState(false);

  const update = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const startNew = () => { setForm(blank()); setEditingId(null); setShowForm(true); };
  const startEdit = (t) => {
    setForm({ name: t.name, tenant_id: t.tenant_id, client_id: t.client_id, client_secret: "", domain: t.domain || "" });
    setEditingId(t.id); setShowForm(true);
  };

  const save = async () => {
    if (!form.tenant_id || !form.client_id || (!editingId && !form.client_secret)) {
      toast.error("Tenant ID, Client ID and Secret are required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/tenants/${editingId}`, form);
        toast.success("Tenant updated");
      } else {
        const r = await api.post(`/tenants`, form);
        toast.success(`Added tenant: ${r.data.name}`);
        setTenantId(r.data.id);
      }
      await refreshTenants();
      setShowForm(false);
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const test = async (id) => {
    setTesting(id);
    try {
      const r = await api.post(`/tenants/${id}/test`);
      toast.success(`✓ ${r.data.tenant_name} — ${r.data.user_count} users — ${(r.data.domains || []).join(", ")}`);
    } catch (e) {
      toast.error("Connection failed: " + (e.response?.data?.detail || e.message));
    } finally { setTesting(null); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Remove tenant "${t.name}"? Its templates / banners / settings will be orphaned (not deleted).`)) return;
    try {
      await api.delete(`/tenants/${t.id}`);
      toast.success("Tenant removed");
      await refreshTenants();
    } catch (e) {
      toast.error("Failed: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="p-8 max-w-4xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-widest mb-1">
            <Building2 size={14} /> Multi-tenant
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Tenants</h1>
          <p className="text-slate-400 mt-1 text-sm max-w-2xl">
            Each tenant is a separate Microsoft 365 organisation with its own users, templates, brand settings and banners. Add as many as you manage.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowGuide(g => !g)} className="btn" data-testid="toggle-guide">
            <BookOpen size={14} /> {showGuide ? "Hide" : "Setup"} guide
          </button>
          <button onClick={startNew} className="btn btn-primary" data-testid="add-tenant">
            <Plus size={14} /> Add Tenant
          </button>
        </div>
      </div>

      <SetupGuide open={showGuide} onClose={() => setShowGuide(false)} />

      {showForm && (
        <div className="glass p-5 mb-4">
          <div className="text-sm font-semibold mb-3">{editingId ? "Edit Tenant" : "Add New Tenant"}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><div className="label">Display name (optional, auto-filled from M365)</div>
              <input className="input" value={form.name} onChange={e => update("name", e.target.value)} placeholder="e.g. Acme Ltd" data-testid="tenant-name" /></div>
            <div><div className="label">Domain</div>
              <input className="input" value={form.domain} onChange={e => update("domain", e.target.value)} placeholder="acme.com" data-testid="tenant-domain" /></div>
            <div><div className="label">Azure Tenant ID</div>
              <input className="input" value={form.tenant_id} onChange={e => update("tenant_id", e.target.value)} placeholder="8a8b0880-..." data-testid="tenant-tid" /></div>
            <div><div className="label">Application (Client) ID</div>
              <input className="input" value={form.client_id} onChange={e => update("client_id", e.target.value)} placeholder="b852ef01-..." data-testid="tenant-cid" /></div>
            <div className="md:col-span-2"><div className="label">Client Secret {editingId && <span className="text-slate-500">(leave blank to keep existing)</span>}</div>
              <input className="input" type="password" value={form.client_secret} onChange={e => update("client_secret", e.target.value)} placeholder={editingId ? "Leave blank to keep" : "Paste your client secret"} data-testid="tenant-secret" /></div>
          </div>
          <div className="text-[11px] text-slate-500 mt-3 leading-relaxed">
            Need to create credentials? Follow the same step-by-step you did for the first tenant — Azure Portal → App Registrations → New, add API permissions (User.Read.All, Organization.Read.All, Exchange.ManageAsApp), grant admin consent, assign Exchange Administrator role to the app.
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="save-tenant">
              <Save size={14} /> {saving ? "Verifying credentials…" : (editingId ? "Update" : "Add & Verify")}
            </button>
            <button onClick={() => setShowForm(false)} className="btn">Cancel</button>
          </div>
        </div>
      )}

      <div className="glass overflow-hidden">
        {tenants.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No tenants yet. Click "Add Tenant" to get started.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-400 border-b border-white/5">
              <tr><th className="text-left px-4 py-2.5">Name</th><th className="text-left px-4 py-2.5">Domain</th>
                <th className="text-left px-4 py-2.5">Tenant ID</th><th className="text-right px-4 py-2.5">Actions</th></tr>
            </thead>
            <tbody>
              {tenants.map(t => (
                <tr key={t.id} className={`border-b border-white/5 ${t.id === tenantId ? "bg-emerald-500/[0.04]" : ""}`} data-testid={`tenant-row-${t.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {t.id === tenantId && <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />}
                      <span className="font-medium">{t.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{t.domain || <span className="text-slate-600">—</span>}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-slate-400">{t.tenant_id}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      {t.id !== tenantId && (
                        <button onClick={() => setTenantId(t.id)} className="btn !py-1 !px-2 text-[11px]" data-testid={`switch-${t.id}`}>Switch to</button>
                      )}
                      <button onClick={() => test(t.id)} disabled={testing === t.id} className="btn !py-1 !px-2 text-[11px]" data-testid={`test-${t.id}`}>
                        {testing === t.id ? "Testing…" : "Test"}
                      </button>
                      <button onClick={() => startEdit(t)} className="btn !py-1 !px-2 text-[11px]" data-testid={`edit-${t.id}`}>Edit</button>
                      {tenants.length > 1 && (
                        <button onClick={() => remove(t)} className="btn btn-danger !py-1 !px-2 text-[11px]" data-testid={`remove-${t.id}`}><Trash2 size={11} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <BackupRestore />
    </div>
  );
}
